import React, { useState } from "react";
import { Dialog as DialogPrimitive, VisuallyHidden } from "radix-ui";
import cn from "classnames";
import Clear from "storefront/components/Icons/Navigation/X";
import Chevron from "storefront/components/Icons/Navigation/Chevron";
import Image from "storefront/components/ImageV2";
import Title2 from "storefront/components/Title2";
import Callout from "storefront/components/Callout";
import { FlashBannerType } from "storefront/lib/flash";
import { Variant as PersistentVariant } from "storefront/components/Banners/PersistentBanner";

import { useReturnFocusToOpener } from "./useReturnFocusToOpener";
import styles from "./Modal.module.scss";

// Radix doesn't render the overlay for non-modal dialogs, and the content is nested inside it.
type Props = Omit<
  React.ComponentProps<typeof DialogPrimitive.Root>,
  "modal"
> & {
  onAfterOpen?: () => void;
  onRequestClose?: () => void;
};

/**
 * Modal Components
 *
 * These components provide a flexible and accessible modal implementation using Radix UI's Dialog primitives.
 *
 * Usage:
 * - Wrap your modal logic with the `Modal` (ModalRoot) component.
 * - Place a `ModalTrigger` inside the `Modal` to open the modal.
 * - Place a `ModalContent` inside the `Modal` to render the modal's content.
 * - Optionally use `ModalTitle`, `ModalDescription`, and `ModalClose` for enhanced accessibility and UI.
 * - The open/closed state of the modal can be controlled externally by passing the `open` prop to the root modal component (`Modal`) and using the onAfterOpen/onRequestClose callbacks.
 * - If not provided, the modal manages its own open/close logic automatically.
 *
 * Important:
 * - Both a trigger (`ModalTrigger`) and content (`ModalContent`) must be used within the `Modal` root for the modal to function correctly.
 * - By default, clicking outside the modal content will close the modal. If you need to disable this behavior (e.g., for critical alerts), use the `AlertModal` component instead.
 *
 * Example:
 * ```
 * <Modal>
 *   <ModalTrigger>Open Modal</ModalTrigger>
 *   <ModalContent>
 *     <ModalTitle>Title</ModalTitle>
 *     <ModalDescription>Description</ModalDescription>
 *     <ModalClose />
 *   </ModalContent>
 * </Modal>
 * ```
 *
 * @component Modal
 * @description
 * Root component for the modal dialog. Wraps Radix DialogPrimitive.Root and manages open/close events.
 * Use this as the parent for all modal-related components.
 *
 * @prop {() => void} [onAfterOpen] - Callback fired after the modal opens.
 * @prop {() => void} [onRequestClose] - Callback fired after the modal closes.
 * @note To pass custom children to the trigger or close button, use the `asChild` prop.
 */

const ModalRoot = ({
  children,
  onAfterOpen,
  onRequestClose,
  ...props
}: Props) => {
  const handleOpenChange = (open: boolean) => {
    if (open) {
      onAfterOpen?.();
    }
    if (!open) {
      onRequestClose?.();
    }
  };

  return (
    <DialogPrimitive.Root onOpenChange={handleOpenChange} {...props}>
      {children}
    </DialogPrimitive.Root>
  );
};

/**
 * @component ModalTrigger
 * @description
 * Wrapper for the modal trigger button. Use this to open the modal.
 * Pass `asChild` to render a custom trigger element.
 */
const ModalTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof DialogPrimitive.Trigger>
>(({ ...props }, ref) => <DialogPrimitive.Trigger ref={ref} {...props} />);
ModalTrigger.displayName = "ModalTrigger";

/**
 * @component ModalContent
 * @description
 * Wrapper for the modal content. Renders the modal overlay and content area.
 */
const ModalContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof DialogPrimitive.Content>
>(({ className, onFocus, onCloseAutoFocus, ...props }, ref) => {
  const autoFocusHandlers = useReturnFocusToOpener({
    onFocus,
    onCloseAutoFocus,
  });

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={styles.overlay}
        data-testid="modal-overlay"
      >
        <DialogPrimitive.Content
          ref={ref}
          className={cn(styles.content, className)}
          {...autoFocusHandlers}
          {...props}
        />
      </DialogPrimitive.Overlay>
    </DialogPrimitive.Portal>
  );
});
ModalContent.displayName = "ModalContent";

/**
 * @component ModalTitle
 * @description
 * Wrapper for the modal title. Use for accessible modal headings.
 * Pass `asChild` to render a custom title element.
 * Pass `hidden` to visually hide the title while keeping it accessible to screen readers.
 */
const ModalTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentProps<typeof DialogPrimitive.Title>
>(({ hidden, ...props }, ref) => {
  if (hidden) {
    return (
      <VisuallyHidden.Root asChild>
        <DialogPrimitive.Title ref={ref} {...props} />
      </VisuallyHidden.Root>
    );
  }

  return <DialogPrimitive.Title ref={ref} {...props} />;
});
ModalTitle.displayName = "ModalTitle";

/**
 * @component ModalDescription
 * @description
 * Wrapper for the modal description. Use for accessible modal descriptions.
 * Pass `asChild` to render a custom description element.
 * Pass `hidden` to visually hide the description while keeping it accessible to screen readers.
 */
const ModalDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<typeof DialogPrimitive.Description>
>(({ hidden, ...props }, ref) => {
  if (hidden) {
    return (
      <VisuallyHidden.Root asChild>
        <DialogPrimitive.Description ref={ref} {...props} />
      </VisuallyHidden.Root>
    );
  }

  return <DialogPrimitive.Description ref={ref} {...props} />;
});
ModalDescription.displayName = "ModalDescription";

/**
 * @component ModalClose
 * @description
 * Wrapper for the modal close button. Renders a default close icon if no children are provided.
 * Pass `asChild` to render a custom close button or element.
 */
const ModalClose = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof DialogPrimitive.Close>
>(({ ...props }, ref) => (
  <DialogPrimitive.Close asChild ref={ref} {...props}>
    {props.children ? (
      props.children
    ) : (
      <button
        className={styles.closeButton}
        type="button"
        aria-label="Close"
        data-testid="modal-close"
      >
        <Clear />
      </button>
    )}
  </DialogPrimitive.Close>
));
ModalClose.displayName = "ModalClose";

type LeftNavigationArrowProps = {
  onLeftArrowClick: () => void;
};

const LeftNavigationArrow = ({
  onLeftArrowClick,
}: LeftNavigationArrowProps) => (
  <div className={styles.headerLeftArrowContainer}>
    <button
      onClick={onLeftArrowClick}
      className={styles.headerLeftArrow}
      title="Back"
      type="button"
      data-testid="modal-left-arrow"
    >
      <Chevron height="15" width="15" left />
    </button>
  </div>
);

type HeaderProps = {
  imgSrc?: string;
  imgAlt?: string;
  title: React.ReactNode;
  onLeftArrowClick?: () => void;
  hideCloseButton?: boolean;
  className?: string;
};

/**
 * `ModalHeader` is a composable component designed as a replacement for `../Modal/WithHeader.tsx`,
 * providing a flexible and customizable modal header with similar props and styles.
 *
 * @param imgSrc - Optional image source URL to display in the header.
 * @param imgAlt - Optional alt text for the header image.
 * @param title - The title text to display in the modal header.
 * @param onLeftArrowClick - Optional callback for the left navigation arrow button; if provided, the arrow is shown.
 * @param showCloseButton - Whether to show the close button in the header (default: `true`).
 * @param className - Optional additional CSS class names for the header container.
 *
 * @remarks
 * - The header supports an optional image, a title, a left navigation arrow, and a close button.
 * - Designed to be composable and easily integrated into modal layouts, following the styles and prop conventions of `WithHeader`.
 */
const ModalHeader = ({
  imgSrc,
  imgAlt,
  title,
  onLeftArrowClick,
  hideCloseButton = false,
  className,
}: HeaderProps) => (
  <div className={cn(styles.header, className)}>
    {onLeftArrowClick ? (
      <LeftNavigationArrow onLeftArrowClick={onLeftArrowClick} />
    ) : null}
    {imgSrc ? (
      <div className={styles.headerImageContainer}>
        <Image
          className={styles.headerCoverImage}
          fill
          sizes="50"
          src={imgSrc}
          alt={imgAlt ?? ""}
          rotate={0}
        />
      </div>
    ) : null}
    <ModalTitle asChild>
      <Title2>{title}</Title2>
    </ModalTitle>
    {!hideCloseButton ? (
      <ModalClose asChild>
        <div className={styles.headerCloseButtonContainer}>
          <button
            className={styles.headerCloseButton}
            type="button"
            aria-label="Close"
            data-testid="modal-close"
          >
            <Clear />
          </button>
        </div>
      </ModalClose>
    ) : null}
  </div>
);

type FlashMessageStatus = "displayed" | "dismissing" | "hidden";

type ModalFlashType = FlashBannerType | Extract<PersistentVariant, "infoTint">;

type ModalFlashProps = {
  message: React.ReactNode;
  type: ModalFlashType;
  autoDismiss?: boolean;
  className?: string;
};

/**
 * Displays a flash message inside a modal with optional auto-dismiss functionality.
 *
 * @param {ModalFlashProps} props - The props for ModalFlash.
 * @param {string} props.message - The message to display in the flash.
 * @param {"alert" | "notice" | "info" | "infoTint"} props.type - The type of flash message, which determines its style.
 * @param {boolean} props.autoDismiss - If true, the flash message will automatically dismiss after its animation.
 *
 * The component manages its own display and dismissal state, and will remain visible if `type` is "infoTint" or `autoDismiss` is false.
 * Screen readers announce "alert" flashes immediately and the other types politely.
 */
const ModalFlash = ({
  message,
  type,
  autoDismiss,
  className,
}: ModalFlashProps) => {
  const [flashMessageStatus, setFlashMessageStatus] =
    useState<FlashMessageStatus>("displayed");

  const handleOnAnimationEnd = () => {
    const canDismiss = type !== "infoTint";
    /* If ModalFlash is not auto-dismissible, we want the flash message
    to stay on the screen */
    if (!canDismiss || !autoDismiss) return;

    switch (flashMessageStatus) {
      case "dismissing":
        setFlashMessageStatus("hidden");
        break;

      case "displayed":
        setFlashMessageStatus("dismissing");
        break;

      default:
        break;
    }
  };

  return (
    <div
      role={type === "alert" ? "alert" : "status"}
      className={cn(styles.flash, className, {
        [styles.alert]: type === "alert",
        [styles.notice]: type === "notice",
        [styles.info]: type === "info",
        [styles.infoTint]: type === "infoTint",
        [styles.dismissing]: flashMessageStatus === "dismissing",
        [styles.hidden]: flashMessageStatus === "hidden",
      })}
      onAnimationEnd={handleOnAnimationEnd}
    >
      <Callout className={styles.flashMessage}>{message}</Callout>
    </div>
  );
};

export {
  ModalRoot as Modal,
  ModalTrigger,
  ModalContent,
  ModalTitle,
  ModalDescription,
  ModalClose,
  ModalHeader,
  ModalFlash,
};
